import { z } from 'zod';

/**
 * Shape of a client-side error report POSTed to /api/errors/client.
 * Shared by the browser reporter and the API route (validated server-side).
 */
export const clientErrorSchema = z.object({
  name: z.string().max(200).optional(),
  message: z.string().min(1).max(2000),
  stack: z.string().max(8000).optional(),
  componentStack: z.string().max(8000).optional(),
  url: z.string().max(2000).optional(),
  digest: z.string().max(200).optional(),
  severity: z.enum(['error', 'fatal']).optional(),
  context: z.record(z.string(), z.unknown()).optional(),
});

export type ClientErrorPayload = z.infer<typeof clientErrorSchema>;

/**
 * Known-benign browser noise that must NOT be reported or logged. These are not
 * real app faults — reporting them floods /api/errors/client and the #Hub Errors
 * Slack channel (the ResizeObserver loop messages alone were ~10k events in a
 * week). Match is case-insensitive substring on the error message. Enforced on
 * BOTH sides: the browser reporter (skips the POST) and the API route (skips the
 * log), so a stale client bundle can't reintroduce the noise.
 */
export const IGNORED_CLIENT_ERROR_PATTERNS: readonly string[] = [
  // Benign ResizeObserver quirk fired by browsers (Chrome/Safari) when an
  // observer callback mutates layout. No user impact, no fix needed.
  // https://stackoverflow.com/q/49384120
  'resizeobserver loop completed with undelivered notifications',
  'resizeobserver loop limit exceeded',
];

export function isIgnoredClientError(message: string | undefined): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return IGNORED_CLIENT_ERROR_PATTERNS.some((p) => lower.includes(p));
}
